import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Avatar, Button, Chip, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, 
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, IconButton, Divider, InputAdornment, Stack
} from '@mui/material';
import { 
  QrCodeScanner, Add, CheckCircle, Warning, Visibility, Delete,
  Inventory, RequestPage, History, Info, Print, Search, FilterList
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

const Crotales = () => {
  const queryClient = useQueryClient();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const [nuevoCrotalDialog, setNuevoCrotalDialog] = useState(false);
  const [verDetallesDialog, setVerDetallesDialog] = useState(false);
  const [crotalForm, setCrotalForm] = useState({ cantidad: 1, tipo_animal: 'bovino' });
  const [selectedCrotal, setSelectedCrotal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');

  const { data: crotalesData, isLoading } = useQuery('crotales', async () => {
    const res = await axios.get('/api/crotales_solicitudes');
    return res.data;
  });

  const { data: stats, isLoading: statsLoading } = useQuery(['dashboard', 'stats'], async () => {
    const res = await axios.get('/api/dashboard/stats');
    return res.data;
  });

  const crotales = Array.isArray(crotalesData) ? crotalesData : [];

  const createCrotal = useMutation(async (data) => {
    await axios.post('/api/crotales_solicitudes', data);
  }, { onSuccess: () => { queryClient.invalidateQueries('crotales'); setNuevoCrotalDialog(false); showMessage('✅ Solicitud de identificación enviada'); } });

  const delCrotal = useMutation(async (id) => {
    await axios.delete(`/api/crotales_solicitudes/${id}`);
  }, { onSuccess: () => { queryClient.invalidateQueries('crotales'); showMessage('🗑️ Solicitud cancelada'); } });

  const showMessage = (msg) => { setSnackbarMessage(msg); setOpenSnackbar(true); };

  const filteredCrotales = crotales.filter(c => {
    const searchLow = searchTerm.toLowerCase();
    const idStr = String(c.id);
    const matchesSearch = idStr.includes(searchLow) || 
                          `#SOL-${c.id}`.toLowerCase().includes(searchLow) || 
                          (c.tipo_animal || '').toLowerCase().includes(searchLow);
    const matchesStatus = filterStatus === 'todos' || c.estado === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const updateCrotalStatus = useMutation(async ({ id, estado }) => {
    await axios.put(`/api/crotales_solicitudes/${id}`, { estado });
  }, { 
    onSuccess: () => { 
      queryClient.invalidateQueries('crotales'); 
      showMessage('✅ Estado de solicitud actualizado');
      setVerDetallesDialog(false);
    } 
  });

  const pendientes = crotales.filter(c => c.estado === 'pendiente');
  const entregados = crotales.filter(c => c.estado === 'entregado' || c.estado === 'aprobado');

  const handlePrintLabel = (item) => {
    const printWindow = window.open('', '_blank');
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=BASERRI-SOL-${item.id}-${item.tipo_animal}`;
    printWindow.document.write(`
      <html>
        <head>
          <title>Etiqueta de Identificación</title>
          <style>
            body { font-family: sans-serif; text-align: center; padding: 20px; }
            .label { border: 2px solid black; padding: 15px; width: 300px; margin: 0 auto; }
            h2 { margin: 0; color: #1e293b; }
            p { margin: 5px 0; font-weight: bold; }
          </style>
        </head>
        <body onload="window.print();window.close()">
          <div class="label">
            <h2>BASERRI-ADITU</h2>
            <p>ID: #SOL-${item.id}</p>
            <p>TIPO: ${item.tipo_animal.toUpperCase()}</p>
            <img src="${qrUrl}" />
            <p>Control Oficial Eusko Jaurlaritza</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (isLoading || statsLoading) return <Box sx={{ p: 4, mt: 8, textAlign: 'center' }}><Typography variant="h6">Sincronizando registros de identificación...</Typography></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      <style>{`
        @media print { .no-print { display: none !important; } }
      `}</style>
      
      {/* Header */}
      <Box className="no-print" sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)', letterSpacing: '-0.02em' }}>
            Identificación Oficial
          </Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)', fontWeight: 500 }}>
            Gestión de crotales RFID y peticiones a la administración.
          </Typography>
        </Box>
        <Button
          className="luxury-button luxury-button-primary"
          variant="contained"
          startIcon={<Add />}
          onClick={() => setNuevoCrotalDialog(true)}
        >
          Solicitar Material
        </Button>
      </Box>

      {/* KPI Ribbons */}
      <Grid container spacing={3} sx={{ mb: 4 }} className="no-print">
        {[
          { label: 'Crotales Entregados', value: entregados.reduce((a, b) => a + parseInt(b.cantidad || 0), 0), color: 'var(--emerald-main)', icon: <CheckCircle />, desc: 'Disponibles para asignar' },
          { label: 'Tramitaciones', value: pendientes.length, color: 'var(--alert-gold)', icon: <RequestPage />, desc: 'En espera de validación' },
          { label: 'Stock Almacén', value: stats?.crotalesDisponibles || 14, color: 'var(--navy-main)', icon: <Inventory />, desc: 'Identificadores físicos' },
        ].map((s, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card className="premium-card">
              <CardContent sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color, width: 56, height: 56 }}>{s.icon}</Avatar>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>{s.value}</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: s.color, textTransform: 'uppercase' }}>{s.label}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>{s.desc}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Search & Filters */}
      <Card className="premium-card no-print" sx={{ mb: 4, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField 
              fullWidth 
              size="small" 
              placeholder="Buscar por referencia o especie..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                sx: { borderRadius: '12px' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select value={filterStatus} label="Estado" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="todos">Todos los Estados</MenuItem>
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="aprobado">Aprobado</MenuItem>
                <MenuItem value="entregado">Entregado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="outlined" startIcon={<Print />} onClick={() => window.print()} sx={{ borderRadius: '12px', fontWeight: 700 }}>Exportar Listado</Button>
          </Grid>
        </Grid>
      </Card>

      {/* Main Content Split */}
      <Grid container spacing={4}>
        <Grid item xs={12} lg={4} className="no-print">
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: 'var(--navy-main)' }}>Información Técnica</Typography>
          <Card className="premium-card accent-navy">
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <QrCodeScanner sx={{ fontSize: 60, color: 'var(--navy-main)', opacity: 0.8, mb: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Tecnología RFID Dual</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'var(--slate-dark)', mb: 2, lineHeight: 1.6 }}>
                Nuestros crotales cumplen con la normativa europea integrando sensores UHF-NFC para trazabilidad automatizada a distancia.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <Info sx={{ color: '#0369a1' }} fontSize="small" />
                <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 600 }}>Plazo de entrega estimado: 3-5 días hábiles.</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: 'var(--navy-main)' }}>Historial de Solicitudes y Stock</Typography>
          <Card className="premium-card">
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Referencia</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Especie</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Cantidad</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCrotales.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'var(--slate-light)' }}>Sin resultados para el filtro aplicado.</TableCell></TableRow>
                  ) : filteredCrotales.map(c => (
                    <TableRow key={c.id} hover onClick={() => { setSelectedCrotal(c); setVerDetallesDialog(true); }} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ fontWeight: 800, color: 'var(--forest-main)' }}>#SOL-{c.id}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{c.tipo_animal}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{c.cantidad} uds.</TableCell>
                      <TableCell>
                        <Chip 
                          label={c.estado} 
                          size="small" 
                          sx={{ 
                            fontWeight: 800, 
                            textTransform: 'uppercase', 
                            fontSize: '9px',
                            bgcolor: c.estado === 'entregado' ? 'var(--alert-mint)' : (c.estado === 'pendiente' ? '#fffbeb' : 'var(--slate-bg)'),
                            color: c.estado === 'entregado' ? 'var(--emerald-main)' : (c.estado === 'pendiente' ? 'var(--alert-gold)' : 'inherit')
                          }} 
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton size="small" onClick={() => { setSelectedCrotal(c); setVerDetallesDialog(true); }}><Visibility fontSize="small" /></IconButton>
                          {(c.estado === 'entregado' || c.estado === 'aprobado') && (
                            <IconButton size="small" color="primary" onClick={() => handlePrintLabel(c)}><Print fontSize="small" /></IconButton>
                          )}
                          {c.estado === 'pendiente' && (
                            <IconButton size="small" color="error" onClick={() => delCrotal.mutate(c.id)}><Delete fontSize="small" /></IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog: Nueva Solicitud */}
      <Dialog open={nuevoCrotalDialog} onClose={() => setNuevoCrotalDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Solicitar Identificadores</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>TIPO DE GANADO</Typography>
              <FormControl fullWidth sx={{ mt: 0.5 }}>
                <Select value={crotalForm.tipo_animal} onChange={(e) => setCrotalForm({...crotalForm, tipo_animal: e.target.value})}>
                  <MenuItem value="bovino">Bovino / Vacuno</MenuItem>
                  <MenuItem value="ovino">Ovino / Ovejas</MenuItem>
                  <MenuItem value="caprino">Caprino / Cabras</MenuItem>
                  <MenuItem value="porcino">Porcino / Cerdos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>CANTIDAD REQUERIDA</Typography>
              <TextField fullWidth type="number" placeholder="Ej. 10" value={crotalForm.cantidad} onChange={(e) => setCrotalForm({...crotalForm, cantidad: parseInt(e.target.value) || 1})} sx={{ mt: 0.5 }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setNuevoCrotalDialog(false)} sx={{ fontWeight: 700, color: 'var(--slate-light)' }}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => createCrotal.mutate(crotalForm)}>Tramitar Pedido</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={verDetallesDialog} onClose={() => setVerDetallesDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '32px' } }}>
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: 'var(--navy-main)' }}>Ficha de Identificación</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--slate-light)' }}>Nº TRACKING: #SOL-{selectedCrotal?.id}</Typography>
            </Box>
            <Chip 
              label={selectedCrotal?.estado?.toUpperCase()} 
              color={selectedCrotal?.estado === 'pendiente' ? 'warning' : 'success'}
              sx={{ fontWeight: 900 }} 
            />
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', display: 'block' }}>ESPECIE DESTINO</Typography>
              <Typography variant="body1" sx={{ fontWeight: 800, textTransform: 'capitalize' }}>{selectedCrotal?.tipo_animal}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', display: 'block' }}>VOLUMEN TOTAL</Typography>
              <Typography variant="body1" sx={{ fontWeight: 800 }}>{selectedCrotal?.cantidad} Unidades</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', display: 'block' }}>FECHA DE SOLICITUD</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedCrotal?.fecha_solicitud}</Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4, p: 3, bgcolor: '#f8fafc', borderRadius: '24px', textAlign: 'center', border: '2px dashed var(--border-color)' }}>
            <img 
              src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=BASERRI-SOL-${selectedCrotal?.id}`} 
              alt="QR Code"
              style={{ width: 120, height: 120, marginBottom: 16 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--slate-dark)' }}>
              Código de Trazabilidad Seguro
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--slate-light)', mt: 0.5 }}>
              Este código vincula el material físico con el registro digital del EAD.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            <Button fullWidth variant="outlined" sx={{ borderRadius: '12px', py: 1.5, fontWeight: 800 }} onClick={() => setVerDetallesDialog(false)}>Cerrar</Button>
            {selectedCrotal?.estado === 'pendiente' && (
              <Button fullWidth variant="contained" color="success" sx={{ borderRadius: '12px', fontWeight: 800 }} onClick={() => updateCrotalStatus.mutate({ id: selectedCrotal.id, estado: 'aprobado' })}>Aprobar</Button>
            )}
            {(selectedCrotal?.estado === 'entregado' || selectedCrotal?.estado === 'aprobado') && (
              <Button fullWidth variant="contained" className="luxury-button luxury-button-primary" startIcon={<Print />} onClick={() => handlePrintLabel(selectedCrotal)}>Imprimir</Button>
            )}
          </Stack>
        </Box>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px', fontWeight: 800 }} onClose={() => setOpenSnackbar(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Crotales;
